import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  OnInit,
  HostBinding,
  OnDestroy,
  DOCUMENT,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { AppSelectors } from 'projects/semeris/src/store/app/app.selectors';
import { DatapointFieldListSelectors } from 'projects/semeris/src/store/datapoint-field-list/datapoint-field-list.selectors';
import { DealActions } from 'projects/semeris/src/store/deal/deal.actions';
import { DealSelectors } from 'projects/semeris/src/store/deal/deal.selectors';
import { DocumentViewerActions } from 'projects/semeris/src/store/document-viewer/document-viewer.actions';
import { ExtractActions } from 'projects/semeris/src/store/extract/extract.actions';
import { ExtractSelectors } from 'projects/semeris/src/store/extract/extract.selectors';
import { MenuActions } from 'projects/semeris/src/store/menu/menu.actions';
import { MenuSelectors } from 'projects/semeris/src/store/menu/menu.selectors';
import { MenuNode, MenuNodeTypes } from 'projects/semeris/src/store/menu/menu.state';
import { RootState } from 'projects/semeris/src/store/root-state.model';
import { UserProfileActions } from 'projects/semeris/src/store/user-profile/user-profile.actions';
import { UserProfileSelectors } from 'projects/semeris/src/store/user-profile/user-profile.selectors';
import { Observable, combineLatest, fromEvent, Subject } from 'rxjs';
import {
  map,
  filter,
  tap,
  distinctUntilChanged,
  takeUntil,
  withLatestFrom,
  take,
  switchMap,
  delay,
  shareReplay,
} from 'rxjs/operators';

import { AssetService } from '../../../asset/service/asset.service';
import { getDocumentTransactionLibraryId } from '../../../document/constants/document-helper';
import { FEEDBACK_TEMPLATE_MAP } from '../../../feedback/components/feedback-modal/feedback-modal.component';
import { Asset } from '../../../home/models/asset.model';
import { SemerisRoutes } from '../../../shared/constants/routes';
import { FastTooltipDirective } from '../../../shared/directives/fast-tooltip.directive';
import { HasAssetDirective } from '../../../shared/directives/has-asset.directive';
import { Permission } from '../../../shared/models/api/permissions.enum';
import { IIdName } from '../../../shared/models/common/i-id-name';
import { QualityTrafficLights } from '../../../shared/models/common/quality-traffic-lights.enum';
import { SafePipe } from '../../../shared/pipes/safe.pipe';
import { DeploymentSwitchService } from '../../../shared/services/deployment/deployment-switch.service';
import { FileService } from '../../../shared/services/file/file.service';
import { PermissionService } from '../../../shared/services/permission.service';
import { IconButtonDirective } from '../../../ui/button/icon-button/icon-button.directive';
import { AssetSelectorComponent } from '../asset-selector/asset-selector.component';
import { SidenavItemComponent } from '../sidenav-item/sidenav-item.component';

@Component({
  imports: [
    CommonModule,
    MatIconModule,
    RouterModule,
    SidenavItemComponent,
    FastTooltipDirective,
    AssetSelectorComponent,
    HasAssetDirective,
    SafePipe,
    IconButtonDirective,
  ],
  selector: 'semeris-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class SidenavComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly fileService = inject(FileService);
  private readonly permissionService = inject(PermissionService);
  private readonly assetService = inject(AssetService);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly deploymentSwitch = inject(DeploymentSwitchService);

  /** Advertise `next` from classic only, and only on a stage that actually has a `next` frontend. */
  private readonly showNextTeasers =
    this.deploymentSwitch.variant === 'classic' && (this.deploymentSwitch.isPaired || this.deploymentSwitch.isLocal);
  public readonly canOpenNext = this.deploymentSwitch.isPaired;

  public routes = SemerisRoutes;

  public treeControl = new NestedTreeControl<MenuNode>((node) => node.children);
  public dataSource$: Observable<MenuNode[]>;

  public assets$: Observable<Asset[]>;

  public link$: Observable<string[]>;

  public mode$: Observable<string>;
  public extended$: Observable<boolean | null>;
  public showOverlay$: Observable<boolean>;

  public logoUrl$: Observable<string>;

  public sidenavList$: Observable<IIdName[]>;

  private readonly destorySubject$ = new Subject<void>();

  @HostBinding('class') class: 'extended' | 'shrinked' | '' = '';

  private get extended(): boolean {
    return this.class === 'extended';
  }

  private get shrinked(): boolean {
    return this.class === 'shrinked';
  }

  public ngOnInit(): void {
    this.assets$ = this.permissionService.hasAnyPermission$.pipe(
      switchMap((hasAnyPermission) =>
        hasAnyPermission ? this.store.select(AppSelectors.assets) : this.assetService.getPublicAssets()
      )
    );

    this.link$ = combineLatest([
      this.store.select(AppSelectors.assets),
      this.store.select(UserProfileSelectors.profile),
    ]).pipe(
      map(([assets, profile]) => {
        if (profile.blocked) {
          return ['/', SemerisRoutes.blocked];
        } else if (!assets?.length) {
          return ['/', SemerisRoutes.welcome];
        }

        return ['/', SemerisRoutes.home.root];
      })
    );

    this.extended$ = this.store.select(AppSelectors.sidenavExtended);

    fromEvent(this.document, 'click')
      .pipe(
        takeUntil(this.destorySubject$),
        withLatestFrom(this.store.select(AppSelectors.breakPointId)),
        filter(([ev, breakpointId]) => breakpointId !== 'large' && this.extended),
        tap(() => {
          this.store.dispatch(AppActions.SetExtended({ sidenavExtended: false }));
        })
      )
      .subscribe();

    this.showOverlay$ = combineLatest([
      this.store.select(AppSelectors.breakPointId),
      this.store.select(AppSelectors.sidenavExtended),
    ]).pipe(map(([breakpointId, extended]) => extended && breakpointId !== 'large'));

    this.mode$ = combineLatest([this.store.select(AppSelectors.breakPointId), this.extended$]).pipe(
      delay(0), // Valid solution: https://blog.angular-university.io/angular-debugging/
      distinctUntilChanged((a, b) => isEqual(a, b)),
      map(([breakPoint, isExtended]) => {
        let extended = isExtended;
        let result: 'large' | 'small' | 'extra-small' | string;

        if (isExtended === true) {
          result = 'large';
        } else if (isExtended === false) {
          result = breakPoint === 'extra-small' ? breakPoint : 'small';
        } else {
          extended = breakPoint === 'large';
          result = breakPoint;
        }

        this.class = extended ? 'extended' : 'shrinked';
        return result;
      })
    );

    this.logoUrl$ = this.store.select(UserProfileSelectors.profile).pipe(
      map((profile) => [profile?.logoImageURL, profile?.smallLogoImageURL]),
      switchMap(([logo, smallLogo]) => this.mode$.pipe(map((mode) => (mode === 'small' ? smallLogo : logo))))
    );

    this.dataSource$ = combineLatest([
      this.store.select(MenuSelectors.menuItems).pipe(distinctUntilChanged((a, b) => isEqual(a, b))),
      this.permissionService.hasEveryPermission([Permission.EDITOR]),
      this.store.select(DealSelectors.deal),
      this.store.select(UserProfileSelectors.profile),
    ]).pipe(
      map(([items, isEditor, deal, profile]) => {
        if (profile.blocked) {
          return items.filter((i) => i.accessToBlockedUsers);
        }

        const visibleItems = this.showNextTeasers ? items : items.filter((node) => !node.nextOnly);

        return visibleItems.map((node) => {
          if (!isEditor && deal?.qualityTrafficLight !== QualityTrafficLights.Green && node.icon === 'bar_chart') {
            return {
              ...node,
              children: [],
            };
          }
          return node;
        });
      }),
      distinctUntilChanged((oldMenu, newMenu) => isEqual(oldMenu, newMenu)),
      shareReplay()
    );
  }

  public changeAsset(selectedAssetShortNames: string[]) {
    this.store.dispatch(
      UserProfileActions.UpdateAsset({
        selectedAssetShortNames,
      })
    );
  }

  public toggleMenu(ev: MouseEvent) {
    ev.stopPropagation();
    this.store.dispatch(AppActions.SetExtended({ sidenavExtended: this.shrinked }));
  }

  public onMenuClick(ev: MouseEvent) {
    ev.stopPropagation();
  }

  public onMenuClicked(node: MenuNode) {
    if (node.nextOnlyPath) {
      this.deploymentSwitch.openPeerPath(node.nextOnlyPath);

      return;
    }

    if (node.type === MenuNodeTypes.Button) {
      if (node.shortCode === 'FBK') {
        this.onFeedback();
      }

      if (node.documentShortName) {
        this.store.dispatch(DealActions.SetIsCompare({ isCompare: false }));

        this.store
          .select(DealSelectors.deal)
          .pipe(
            map((d) => d?.transactionId),
            take(1)
          )
          .subscribe((transactionId) => {
            this.store.dispatch(
              DocumentViewerActions.OpenDocument({
                documentId: node.documentShortName,
                documentTransactionLibraryId: getDocumentTransactionLibraryId(node.documentShortName, transactionId),
              })
            );
          });
      }
    }

    if (node.type === MenuNodeTypes.Navigation) {
      this.store.dispatch(MenuActions.SelectMenu({ menu: node.navigateTo }));
    }

    if (node.type === MenuNodeTypes.Collapse) {
      this.store.dispatch(MenuActions.CollapseMenu({ shortCode: node.shortCode }));
    }

    if (node.type === MenuNodeTypes.JsonEditorField) {
      combineLatest([
        this.store.select(DatapointFieldListSelectors.datapointFieldsInRecord),
        this.store.select(ExtractSelectors.list),
        this.store.select(DealSelectors.deal),
      ])
        .pipe(take(1))
        .subscribe(([fieldsByColumnId, extractList, deal]) => {
          const datapointField = fieldsByColumnId?.[node.columnId];
          const transactionId = deal?.transactionId;
          const datapointRecord = extractList?.[node.columnId]?.[transactionId] as
            | { value?: unknown; sourceReferences?: unknown[] }
            | undefined;

          if (!datapointField) {
            return;
          }

          this.store.dispatch(
            ExtractActions.UpdateSelectedDatapointForEdit({
              datapointField,
              datapointValue: datapointRecord?.value as never,
              sourceReferences: (datapointRecord?.sourceReferences as never) || [],
              columnId: node.columnId,
              transactionId,
            })
          );
        });
    }
  }

  public onFeedback() {
    this.store.dispatch(AppActions.OpenFeedback({ ...FEEDBACK_TEMPLATE_MAP.get('feedback') }));
  }

  public ngOnDestroy(): void {
    this.destorySubject$.next(undefined);
    this.destorySubject$.complete();
  }

  private getLogo(isSmall: boolean): Observable<string> {
    return this.fileService
      .download<Blob>('/logo', 'blob', isSmall ? { small: 'true' } : undefined)
      .pipe(map((response: Blob) => URL.createObjectURL(response)));
  }

  public hasChild = (_: number, node: MenuNode) => !!node.children && node.children.length > 0;
}
